
import { defineComponent, PropType, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Tooltip from 'primevue/tooltip';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import LocalizedTextInput from '@/components/fields/LocalizedTextInput.vue';
import { UserState } from '@/composables/User';
import { DEFAULT_CURRENCY } from '@/components/template-builder/utils/constants';
import {
  voucherMethodsValues,
  voucherTypeValues,
} from '@/composables/flows';

export interface VoucherData {
  template_display_reduction_method: number;
  template_display_reduction_descript: Record<string, string>;
  template_display_reduction_type: number;
  template_display_reduction_identifiant: string;
  template_display_reduction_amount: number;
  template_display_reduction_duplicate: string;
  template_display_reduction_minorderamount: number;
  template_display_reduction_validtime: number;
  template_display_reduction_prefix: string;
}

export default defineComponent({
  name: 'VoucherConfig',

  components: {
    Dropdown,
    InputText,
    InputNumber,
    FieldErrors,
    LocalizedTextInput,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    modelValue: {
      type: Object as PropType<VoucherData>,
      required: true,
    },

    formValidation: {
      type: Object,
      default: () => ({}),
    },

    allowAutomaticMethod: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const currency = computed(() => UserState.activeShop?.currency ?? DEFAULT_CURRENCY);

    const VOUCHER_METHODS = voucherMethodsValues;
    const VOUCHER_TYPES = voucherTypeValues;

    // Local reactive copy of the data
    const localData = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value),
    });

    // Voucher method options
    const voucherMethodOptions = computed(() => {
      const options = [];
      if (props.allowAutomaticMethod) {
        options.push({
          label: t('automatedScenarios.actions.sending_channel.vouchers_config.method.options.automatic'),
          value: VOUCHER_METHODS.AUTOMATIC,
        });
      }
      options.push({
        label: t('automatedScenarios.actions.sending_channel.vouchers_config.method.options.manual'),
        value: VOUCHER_METHODS.MANUAL,
      });
      return options;
    });

    // Voucher type options
    const voucherTypeOptions = computed(() => {
      const baseOptions = [
        {
          label: t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.percentage'),
          value: VOUCHER_TYPES.PERCENTAGE,
        },
        {
          label: t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.amount'),
          value: VOUCHER_TYPES.AMOUNT,
        },
        {
          label: t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.shipping_fees'),
          value: VOUCHER_TYPES.SHIPPING_FEES,
        },
      ];

      if (localData.value.template_display_reduction_method === VOUCHER_METHODS.AUTOMATIC) {
        baseOptions.push({
          label: t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.duplicated'),
          value: VOUCHER_TYPES.DUPLICATED,
        });
      }

      return baseOptions;
    });

    // Period of validity options
    const periodOfValidityOptions = computed(() => [
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.one_day'), value: 1 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.two_day'), value: 2 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.three_day'), value: 3 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.four_day'), value: 4 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.five_day'), value: 5 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.six_day'), value: 6 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.one_week'), value: 7 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.two_week'), value: 14 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.three_week'), value: 21 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.four_week'), value: 28 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.five_week'), value: 35 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.six_week'), value: 42 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.seven_week'), value: 49 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.eight_week'), value: 56 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.ten_week'), value: 70 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.twelve_week'), value: 84 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.fourteen_week'), value: 98 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.sixteen_week'), value: 112 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.eighteen_week'), value: 126 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.twenty_week'), value: 140 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.twenty_two_week'), value: 154 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.twenty_four_week'), value: 168 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.twenty_six_week'), value: 182 },
    ]);

    // Reset type to PERCENTAGE if DUPLICATED is selected but method changes to MANUAL
    watch(() => localData.value.template_display_reduction_method, (newMethod) => {
      if (newMethod === VOUCHER_METHODS.MANUAL
        && localData.value.template_display_reduction_type === VOUCHER_TYPES.DUPLICATED) {
        localData.value.template_display_reduction_type = VOUCHER_TYPES.PERCENTAGE;
      }
    });

    return {
      t,
      localData,
      currency,
      voucherMethodOptions,
      voucherTypeOptions,
      periodOfValidityOptions,
      VOUCHER_METHODS,
      VOUCHER_TYPES,
    };
  },
});
